import { useEffect, useState } from "react";
import { PopupModal } from "../../../components/Modal";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import CustomButton from "../../../components/Button";
import Spinner from "../../../components/Spinners";

const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const NAME_EXTENSION_RE = /\.(md|markdown)$/i;

type Props = {
  documentId: string;
  initialName: string;
  onClose: () => void;
  onSaved: (
    documentId: string,
    data: { content?: string; name?: string }
  ) => Promise<unknown>;
};

export function DocumentContentModal({
  documentId,
  initialName,
  onClose,
  onSaved,
}: Props) {
  const [name, setName] = useState(initialName);
  const [content, setContent] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const text = await api.documents.getContent(documentId);
        if (cancelled) return;
        setContent(text);
        setInitialContent(text);
      } catch (err: any) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load document content";
        setLoadError(msg);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const nameChanged = name.trim() !== initialName.trim();
  const contentChanged = content !== initialContent;
  const hasChanges = nameChanged || contentChanged;

  const validate = (): string | null => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return "Name cannot be empty.";
    if (trimmed.length > 255) return "Name must be 255 characters or fewer.";
    if (!NAME_EXTENSION_RE.test(trimmed))
      return "Name must end with .md or .markdown.";
    const bytes = new Blob([content]).size;
    if (bytes > MAX_SIZE_BYTES)
      return `Content is ${(bytes / 1024 / 1024).toFixed(2)} MB. Max is 10 MB.`;
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      ShowToast({ message: err, type: "error" });
      return;
    }
    if (!hasChanges) {
      onClose();
      return;
    }

    const payload: { content?: string; name?: string } = {};
    if (contentChanged) payload.content = content;
    if (nameChanged) payload.name = name.trim();

    setIsSaving(true);
    try {
      await onSaved(documentId, payload);
      onClose();
    } catch {
      // toast already shown by hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    if (hasChanges) {
      const confirmed = window.confirm(
        "Discard unsaved changes?"
      );
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <PopupModal
      onClose={handleClose}
      contentClassName="w-[min(900px,95vw)] mx-auto max-h-[90vh] overflow-hidden !p-0"
    >
      <div className="flex flex-col h-full max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-border-secondary">
          <div className="flex-1 min-w-0">
            <label
              htmlFor="doc-name"
              className="block text-xs font-medium text-content-secondary mb-1"
            >
              Name
            </label>
            <input
              id="doc-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading || isSaving}
              className="w-full text-lg font-semibold text-content-primary bg-transparent border-b border-transparent focus:border-brand-primary focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed px-1 py-1"
              placeholder="document.md"
            />
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Close"
            className="shrink-0 mt-5 text-content-secondary hover:text-content-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto px-6 py-4 bg-surface-tertiary">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner className="!text-brand-primary" />
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <span className="text-red-600 text-sm">{loadError}</span>
              <CustomButton
                onClick={() => {
                  setLoadError(null);
                  setIsLoading(true);
                  api.documents
                    .getContent(documentId)
                    .then((t) => {
                      setContent(t);
                      setInitialContent(t);
                    })
                    .catch((e: any) =>
                      setLoadError(
                        e?.response?.data?.message ||
                          e?.message ||
                          "Failed to load"
                      )
                    )
                    .finally(() => setIsLoading(false));
                }}
              >
                Retry
              </CustomButton>
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={isSaving}
              spellCheck={false}
              className="w-full h-[55vh] resize-none rounded-lg border border-border-secondary bg-surface-primary px-4 py-3 font-mono text-sm leading-6 text-content-primary focus:outline-none focus:border-brand-primary disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="# Section\n\n### Question\n\n**Short answer.**\n\n_Details..._"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-border-secondary bg-surface-primary">
          <div className="text-xs text-content-secondary">
            {contentChanged && (
              <span>
                Saving will trigger re-embedding (~10–15s).
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <CustomButton
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSave}
              disabled={isLoading || isSaving || !!loadError || !hasChanges}
            >
              {isSaving ? <Spinner /> : "Save"}
            </CustomButton>
          </div>
        </div>
      </div>
    </PopupModal>
  );
}
